import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { useAuth } from '../lib/auth';
import { balanceAPI, formatApiError } from '../lib/api';
import { CheckCircle2, Clock, XCircle, Wallet, CreditCard } from 'lucide-react';


export default function Balance() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [shopierProducts, setShopierProducts] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [reqRes, shopierRes] = await Promise.all([
        balanceAPI.myBalanceRequests(),
        balanceAPI.getShopierProducts().catch(() => ({ data: { products: [] } })),
      ]);
      setRequests(reqRes.data);
      setShopierProducts(shopierRes.data.products || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);


  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Bakiye Yönetimi</h1>
          <p className="text-muted-foreground">Shopier ile kartla bakiye yükleyin. Ödeme sonrası bakiyeniz otomatik yüklenir.</p>
        </div>

        {/* Current balance */}
        <div className="aurora-card rounded-3xl p-6 mb-6 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-1"><Wallet className="w-3.5 h-3.5" /> Mevcut Bakiye</p>
              <p className="text-4xl font-bold text-emerald-400 tabular-nums" data-testid="current-balance">{(user?.balance_tl || 0).toFixed(2)} TL</p>
            </div>
          </div>
        </div>



        <div className="aurora-card rounded-3xl p-6 mb-6 border-fuchsia-500/20 bg-fuchsia-500/5">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-fuchsia-400" />
            <h2 className="text-lg font-semibold">Shopier ile Kartla Bakiye Yükle</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Tutarı seçip Shopier ödeme sayfasına geçin. Ödeme sonrası bakiyeniz otomatik olarak yüklenir.</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {shopierProducts.map((p) => (
              p.enabled ? (
                <a key={p.amount} href={p.url} target="_blank" rel="noopener noreferrer" className="text-center px-3 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white font-semibold hover:opacity-90">
                  {p.amount.toFixed(0)} TL Öde
                </a>
              ) : (
                <button key={p.amount} disabled className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground cursor-not-allowed">
                  {p.amount.toFixed(0)} TL
                </button>
              )
            ))}
          </div>
          {shopierProducts.every(p => !p.enabled) && <p className="text-xs text-amber-300 mt-3">Shopier ürün linkleri .env içine eklenince butonlar aktif olur.</p>}
        </div>

        {/* My requests */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Bakiye Talepleri</h2>
          {requests.length === 0 ? (
            <div className="aurora-card rounded-3xl p-8 text-center text-muted-foreground text-sm">
              Henüz bakiye talebiniz yok.
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map(r => (
                <div key={r.request_id} className="aurora-card rounded-2xl p-4 flex items-center justify-between" data-testid={`request-${r.request_id}`}>
                  <div>
                    <p className="font-semibold text-lg tabular-nums">{r.amount.toFixed(2)} TL</p>
                    <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString('tr-TR')}</p>
                    {r.admin_note && <p className="text-xs text-fuchsia-300 mt-1">Admin notu: {r.admin_note}</p>}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    pending:  { Icon: Clock,        label: 'Bekliyor',   cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    approved: { Icon: CheckCircle2, label: 'Onaylandı', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    rejected: { Icon: XCircle,      label: 'Reddedildi', cls: 'bg-rose-500/15 text-rose-400 border-rose-500/30' },
  };
  const v = map[status] || map.pending;
  const Ic = v.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${v.cls}`}>
      <Ic className="w-3 h-3" />{v.label}
    </span>
  );
}

